import type { RefObject } from 'react';
import type { DocRecordView, FieldView, RepeatingGroupView } from '../lib/api';
import { InfoTip } from '../lib/info-tip';
import { browseOutcome, browseOutcomeKey, groupHeading, labels, reasonClassLabel } from '../lib/labels';

/**
 * The parts of a document record that `#doc/<id>` and `#test-extraction` both show (QA-34): the
 * detection block, the field tables, the gate report, the audit trail with the reader notes, and
 * the raw files. One copy, so a change to how a record reads lands on both pages.
 */

export function DetectionBlock({ view }: { view: DocRecordView }) {
  return (
    <dl className="spec">
      <dt>{labels.docRecord.layout}</dt>
      <dd>{view.layout ?? '—'}</dd>
      <dt>{labels.docRecord.xmlCoverage}</dt>
      <dd>{view.xmlCoverage ?? '—'}</dd>
    </dl>
  );
}

/**
 * The header fields the field table shows. Layout shows once: when the detection block above has
 * it, the `layout` field row is dropped.
 */
export function shownFields(view: DocRecordView): FieldView[] {
  return view.layout == null ? view.fields : view.fields.filter((field) => field.name !== 'layout');
}

/** The label the backend gives a value with no confidence signal (`confidence-label.ts`). */
const NO_SIGNAL = 'no confidence signal recorded';

/**
 * QA-113: the cell a gate path names. The gate writes `partidas[0].subd.value`, the row check
 * `partidas[0].subd`, and a header field is `pedimentoNumber` or `pedimentoNumber.value`.
 */
export type CellRef = { group: string; row: number; column: string } | { field: string };

export function parseCellPath(path: string): CellRef | null {
  const parts = path.replace(/\[(\d+)\]/g, '.$1').split('.').filter(Boolean);
  if ((parts.length === 4 || parts.length === 2) && parts[parts.length - 1] === 'value') parts.pop();
  if (parts.length === 3 && /^\d+$/.test(parts[1])) return { group: parts[0], row: Number(parts[1]), column: parts[2] };
  if (parts.length === 1) return { field: parts[0] };
  return null;
}

function cellKey(ref: CellRef): string {
  return 'field' in ref ? ref.field : `${ref.group}.${ref.row}.${ref.column}`;
}

/** A stable DOM id for each value cell, so the verdict line can scroll to it. */
export function cellDomId(ref: CellRef): string {
  return `doc-cell-${cellKey(ref).replace(/[^\w-]/g, '-')}`;
}

export interface AffectedCell {
  path: string;
  key: string;
  /** Set when the cell is on this page; the label then names it the way the page shows it. */
  found?: { id: string; label: string };
}

/**
 * QA-113: the values an escalation is about, the row check's flagged cells first, then the fields
 * below their confidence threshold, once each. Rows are 1-based on screen.
 */
export function affectedCells(view: DocRecordView): AffectedCell[] {
  if (view.provenance.gateVerdict !== 'escalate' || !view.gateReport.available) return [];
  const seen = new Map<string, AffectedCell>();
  for (const path of [...view.gateReport.flaggedCells, ...(view.gateReport.fieldsBelowThreshold ?? [])]) {
    const ref = parseCellPath(path);
    const key = ref ? cellKey(ref) : path;
    if (seen.has(key)) continue;
    seen.set(key, { path, key, found: ref ? locate(view, ref) : undefined });
  }
  return [...seen.values()];
}

function locate(view: DocRecordView, ref: CellRef): AffectedCell['found'] {
  if ('field' in ref) {
    // Only a cell on the page gets a button: a field the page does not show is plain text.
    return shownFields(view).some((field) => field.name === ref.field) ? { id: cellDomId(ref), label: ref.field } : undefined;
  }
  const group = view.repeatingGroups.find((candidate) => candidate.name === ref.group);
  if (!group || ref.row >= group.rows.length || !group.columns.includes(ref.column)) return undefined;
  return { id: cellDomId(ref), label: labels.docRecord.cellInGroup(groupHeading(group.name), ref.row + 1, ref.column) };
}

/** The keys of the cells this page marks and lets the verdict line focus. */
export function markedCells(view: DocRecordView): Set<string> {
  return new Set(affectedCells(view).filter((cell) => cell.found).map((cell) => cell.key));
}

/** Scroll the cell into view and move focus to it. Not a `#` link: the hash is the route. */
function goToCell(id: string) {
  const cell = document.getElementById(id);
  if (!cell) return;
  cell.scrollIntoView({ block: 'center' });
  cell.focus({ preventScroll: true });
}

const NO_MARKS = new Set<string>();

export function FieldTable({ fields, marked = NO_MARKS }: { fields: FieldView[]; marked?: Set<string> }) {
  if (fields.length === 0) return null;
  // QA-39: with no signal on any row the column repeats one sentence, so say it once under the table.
  const hasSignal = fields.some((field) => field.confidenceLabel !== NO_SIGNAL);
  // QA-79: the same for the source. When every row names one source, the column says it 38 times,
  // so say it once under the table.
  const sources = new Set(fields.map((field) => field.source ?? ''));
  const singleSource = sources.size === 1 ? [...sources][0] : '';
  const hasSource = sources.size > 1;
  return (
    <>
    <div className="responsive-table flow-lg">
      <table>
        <thead>
          <tr>
            <th>{labels.docRecord.columns.field}</th>
            <th>{labels.docRecord.columns.value}</th>
            {hasSource && <th>{labels.docRecord.columns.source}</th>}
            {hasSignal && <th>{labels.docRecord.columns.confidence}</th>}
          </tr>
        </thead>
        <tbody>
          {fields.map((field) => {
            const isMarked = marked.has(field.name);
            return (
              <tr key={field.name}>
                <td className="mono">{field.name}</td>
                {/* The extracted value is the one thing on this screen a compliance reader checks
                    character by character. Mono, and never re-formatted. */}
                <td
                  className="mono"
                  id={cellDomId({ field: field.name })}
                  data-flagged={isMarked || undefined}
                  tabIndex={isMarked ? -1 : undefined}
                >
                  {renderValue(field.value)}
                </td>
                {hasSource && <td>{field.source ?? '—'}</td>}
                {hasSignal && <td>{field.confidenceLabel}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    {singleSource && <p className="panel__sub">{labels.docRecord.sourceOnce(singleSource)}</p>}
    {!hasSignal && <p className="panel__sub">{labels.docRecord.noConfidenceSignal}</p>}
    </>
  );
}

export function RepeatingGroupTable({ group, marked = NO_MARKS }: { group: RepeatingGroupView; marked?: Set<string> }) {
  return (
    <section className="subpanel">
      <h3>{groupHeading(group.name)}</h3>
      <div className="responsive-table">
        <table>
          <thead>
            <tr>
              {group.columns.map((column) => (
                <th key={column}>{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {group.rows.map((row, index) => (
              // Repeating-group rows carry no id of their own (a schema field, not this screen's
              // concern); position is stable within one read.
              // eslint-disable-next-line react/no-array-index-key
              <tr key={index}>
                {group.columns.map((column) => {
                  const cell = group.cells?.[index]?.[column];
                  const ref = { group: group.name, row: index, column };
                  const isMarked = marked.has(cellKey(ref));
                  return (
                    <td
                      key={column}
                      className="mono"
                      id={cellDomId(ref)}
                      data-flagged={cell?.flagged || isMarked || undefined}
                      tabIndex={isMarked ? -1 : undefined}
                    >
                      {renderValue(row[column])}
                      {cell?.source && <span className="cell-note"> ({cell.source})</span>}
                      {cell?.confidenceLabel && cell.confidenceLabel !== NO_SIGNAL && (
                        <span className="cell-note">
                          {' '}
                          [{cell.confidenceLabel}
                          {cell.flagged ? `: ${labels.docRecord.cellNotConfirmed}` : ''}]
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value) && value.length === 0) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** The verdict and its reason, as a sentence. `#doc/<id>` shows it under the title; the gate
 *  report of a test run shows it where it opens. */
export function VerdictLine({ view }: { view: DocRecordView }) {
  const verdict = view.provenance.gateVerdict;
  if (!verdict) return null;
  const escalated = verdict === 'escalate';
  const cells = affectedCells(view);
  return (
    <p className="verdict-line">
      <strong className="verdict-line__status" data-verdict={verdict}>
        {browseOutcome(view.provenance)}
      </strong>
      {browseOutcomeKey(view.provenance) === 'review' && (
        <InfoTip label={browseOutcome(view.provenance)} text={labels.documents.browse.needsReviewTip} />
      )}
      {escalated && view.gateReport.available && view.gateReport.reasonClass && (
        <>
          {' '}
          {labels.docRecord.escalatedBecause}: <strong>{reasonClassLabel(view.gateReport.reasonClass)}</strong> (
          <span className="mono">{view.gateReport.reasonClass}</span>)
        </>
      )}
      {cells.length > 0 && (
        <span className="verdict-line__cells">
          {labels.docRecord.valuesToCheck}:{' '}
          {cells.map(({ key, path, found }, index) => (
            <span key={key}>
              {/* A semicolon, as each label already holds commas. */}
              {index > 0 && '; '}
              {found ? (
                <button type="button" className="btn btn--text" onClick={() => goToCell(found.id)}>
                  {found.label}
                </button>
              ) : (
                <span className="mono">{path}</span>
              )}
            </span>
          ))}
        </span>
      )}
    </p>
  );
}

export function GateReportBlock({ view, showReason = true }: { view: DocRecordView; showReason?: boolean }) {
  return (
    <section className="subpanel">
      <h3>{labels.docRecord.gateReportHeading}</h3>
      {!view.gateReport.available ? (
        <p className="notice notice--info">{labels.docRecord.gateReportUnavailable}</p>
      ) : (
        <>
          {showReason && view.provenance.gateVerdict === 'escalate' && view.gateReport.reasonClass && (
            <p>
              {labels.docRecord.escalatedBecause}: <strong>{reasonClassLabel(view.gateReport.reasonClass)}</strong> (
              <span className="mono">{view.gateReport.reasonClass}</span>)
            </p>
          )}
          {view.provenance.gateVerdict === 'escalate' && view.gateReport.flaggedCells.length > 0 && (
            <>
              <h4>{labels.docRecord.flaggedCellsHeading}</h4>
              <ul className="mono">
                {view.gateReport.flaggedCells.map((cell) => (
                  <li key={cell}>{cell}</li>
                ))}
              </ul>
            </>
          )}
          <div className="responsive-table">
            <table>
              <thead>
                <tr>
                  <th>{labels.docRecord.gateColumns.rule}</th>
                  <th>{labels.docRecord.gateColumns.status}</th>
                  <th>{labels.docRecord.gateColumns.message}</th>
                </tr>
              </thead>
              <tbody>
                {view.gateReport.outcomes.map((outcome, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={index}>
                    <td className="mono">{outcome.ruleId}</td>
                    <td>
                      <span className="pill verdict" data-status={outcome.status}>
                        {outcome.status}
                      </span>
                    </td>
                    <td>
                      {outcome.message}
                      {outcome.counterpartDocId && (
                        <>
                          {' '}
                          <a href={`#doc/${outcome.counterpartDocId}`}>{labels.docRecord.counterpart}</a>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

export function AuditTrail({ view }: { view: DocRecordView }) {
  const { provenance } = view;
  return (
    <section className="subpanel">
      <div className="panel__head">
        <h3>{labels.docRecord.auditTrailHeading}</h3>
        {/* QA-84: the links sit at the end edge of the head line. */}
        <span className="action-row end-edge">
          {view.langfuse.traceUrl && (
            <a href={view.langfuse.traceUrl} target="_blank" rel="noreferrer">
              {labels.docRecord.langfuseTrace}
            </a>
          )}
          <a href={view.extractionRulesHref}>{labels.docRecord.viewExtractionRules}</a>
        </span>
      </div>
      {/* A definition list, not a two-column table of one-row groups: these are key/value pairs
          and saying so is what lets a screen reader read "Model id, then the value" rather than
          announcing a table with no header row. */}
      <dl className="spec">
        <dt>{labels.docRecord.audit.skillVersion}</dt>
        <dd>{provenance.skillVersion ?? '—'}</dd>
        <dt>{labels.docRecord.audit.modelId}</dt>
        <dd>{provenance.modelId ?? '—'}</dd>
        <dt>{labels.docRecord.audit.gateVerdict}</dt>
        <dd>{provenance.gateVerdict ?? '—'}</dd>
        <dt>{labels.docRecord.audit.tier}</dt>
        <dd>{provenance.tier ?? '—'}</dd>
        <dt>{labels.docRecord.audit.attempt}</dt>
        <dd>{provenance.attempt}</dd>
        {provenance.reviewerIdentity && (
          <>
            <dt>{labels.docRecord.audit.reviewedBy}</dt>
            <dd>{provenance.reviewerIdentity}</dd>
          </>
        )}
        {provenance.readerNotes && provenance.readerNotes.length > 0 && (
          <>
            <dt>{labels.docRecord.audit.readerNotes}</dt>
            <dd>
              {provenance.readerNotes.map((note) => (
                <div key={note}>{note}</div>
              ))}
            </dd>
          </>
        )}
      </dl>
    </section>
  );
}

export function RawFiles({ view, headingRef }: { view: DocRecordView; headingRef: RefObject<HTMLHeadingElement> }) {
  return (
    <div className="flow-lg">
      {/* A label, a rule, the text. Never a re-drawn terminal window — the operator already has
          one, and a fake one always has the wrong dots. */}
      <div className="raw">
        <h4 ref={headingRef} tabIndex={-1}>
          {labels.docRecord.rawResultFile}
        </h4>
        <pre>{JSON.stringify(view.raw.result, null, 2)}</pre>
      </div>
      <div className="raw">
        <h4>{labels.docRecord.rawProvenanceFile}</h4>
        <pre>{JSON.stringify(view.raw.provenance, null, 2)}</pre>
      </div>
    </div>
  );
}
